import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Clock, Loader2 } from "lucide-react";
import { format } from "date-fns";

export default function TimeSlotSelection({
  selectedDate,
  selectedService,
  doctorFilter,
  formData,
  availableSlots,
  loadingSlots,
  onTimeSlotSelect,
}) {
  // Clear condition variables
  const hasSelectedDate = !!selectedDate;
  const isLoading = loadingSlots;
  const serviceRequiresDoctor = selectedService?.requires_doctor;
  const hasDoctorSelected = !!doctorFilter;
  const hasSlotsToShow = availableSlots.length > 0;

  const renderNoDateSelected = () => (
    <div className="text-center py-8 text-slate-500 border rounded-lg">
      <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
      <p className="text-sm">Please select a date first</p>
    </div>
  );

  const renderLoadingState = () => (
    <div className="text-center py-8 text-slate-500 border rounded-lg">
      <Loader2 className="w-8 h-8 mx-auto mb-2 text-slate-300 animate-spin" />
      <p className="text-sm">Loading available times...</p>
    </div>
  );

  const renderNoSlotsAvailable = () => (
    <div className="text-center py-8 text-slate-500 border rounded-lg">
      <p className="text-sm">No available time slots for this date</p>
    </div>
  );

  const renderTimeSlotButton = (slot, index) => {
    const isSelected = formData.start_time === slot.startTime;

    return (
      <Button
        key={index}
        type="button"
        variant={isSelected ? "default" : "outline"}
        size="sm"
        onClick={() => onTimeSlotSelect(slot)}
        className={isSelected ? "bg-blue-600 text-white" : ""}
        data-testid={`time-slot-${slot.startTime}`}
      >
        <div className="flex flex-col items-center">
          <span>
            {format(new Date(`2024-01-01T${slot.startTime}`), "h:mm a")}
          </span>
        </div>
      </Button>
    );
  };

  const renderTimeSlots = () => (
    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 border rounded-lg" data-testid="time-slots-container">
      {availableSlots.map(renderTimeSlotButton)}
    </div>
  );

  const renderSlotCount = () => {
    if (!hasSelectedDate || !hasSlotsToShow) return null;

    return (
      <div className="text-xs text-slate-500 text-center">
        {availableSlots.length} slots available on{" "}
        {format(selectedDate, "MMM d, yyyy")}
      </div>
    );
  };

  const renderNoDoctorSelected = () => (
    <div className="text-center py-8 text-slate-500 border rounded-lg">
      <p className="text-sm">
        Please select a doctor first to see available times
      </p>
    </div>
  );

  const renderTimeSlotContent = () => {
    if (!hasSelectedDate) return renderNoDateSelected();
    if (serviceRequiresDoctor && !hasDoctorSelected)
      return renderNoDoctorSelected();
    if (isLoading) return renderLoadingState();
    if (!hasSlotsToShow) return renderNoSlotsAvailable();
    return renderTimeSlots();
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Clock className="w-4 h-4" />
        Available Times *
      </Label>

      {renderTimeSlotContent()}
      {renderSlotCount()}
    </div>
  );
}
